import React from "react";
import { Video, Phone } from "lucide-react";
import { buildWhatsAppLink } from "../../lib/api";
import { BANNER_IMAGE, APPOINTMENT_TEXT, CALL_TEXT, heroStats } from "../../lib/home-content";
import Counter from "../Counter";
import useScrollY from "../../lib/useScrollY";

const HeroSection = () => {
  const scrollY = useScrollY();
  // Parallax only matters while hero is in view — cap to avoid huge translations.
  const heroParallax = Math.min(scrollY, 900) * 0.28;

  return (
    <section id="top" className="relative overflow-hidden -mt-px">
      {/* Background image layer — parallax slowed by scroll */}
      <div
        className="absolute left-0 right-0"
        style={{
          top: "-12%",
          bottom: "-12%",
          backgroundImage: `url("${BANNER_IMAGE}")`,
          backgroundSize: "150% auto",
          backgroundPosition: "left 20%",
          backgroundRepeat: "no-repeat",
          transform: `translate3d(0, ${heroParallax}px, 0)`,
          willChange: "transform",
        }}
        aria-hidden="true"
      />
      {/* Thematic gradient overlay — strong on left for text, fades to reveal photo on right */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(15,18,69,0.82) 0%, rgba(15,18,69,0.55) 30%, rgba(15,18,69,0.18) 60%, rgba(15,18,69,0.00) 100%)",
        }}
        aria-hidden="true"
      />
      {/* Bottom fade — makes hero blend seamlessly into the dark About gradient below */}
      <div
        className="absolute inset-x-0 bottom-0 h-48 md:h-64 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(15,18,69,0) 0%, rgba(15,18,69,0.6) 60%, #0F1245 100%)",
        }}
        aria-hidden="true"
      />
      {/* Soft grain for editorial feel */}
      <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 min-h-[88vh] flex flex-col justify-center py-24 md:py-32">
        <div className="max-w-3xl fade-up">
          <div className="overline" style={{ color: "#B7E4F4" }}>Wholesale Only · Since 2007 · New Delhi</div>
          <h1 className="font-serif-display font-light text-[44px] sm:text-[58px] lg:text-[80px] xl:text-[92px] leading-[0.98] tracking-tight mt-6 text-white">
            India&apos;s Trusted Destination<br />
            for <span className="italic text-[#B7E4F4]">Designer Fashion Wholesale.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-[17px] md:text-[18px] text-white/90 leading-relaxed">
            Crafting trend-forward designer suits, co-ord sets, and ethnic wear for retailers worldwide.
          </p>
          <p className="mt-4 max-w-2xl text-[15px] md:text-[16px] text-white/75 leading-relaxed">
            <strong className="text-white font-medium">18+ years</strong> of manufacturing excellence trusted by <strong className="text-white font-medium">20,000+ boutiques</strong> and fashion businesses.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={buildWhatsAppLink(APPOINTMENT_TEXT)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-appointment-btn"
              className="btn-primary !bg-white !text-[#0F1245] !border-white hover:!bg-[#B7E4F4] hover:!border-[#B7E4F4]"
            >
              <Video size={14} strokeWidth={1.6} /> Book an Appointment
            </a>
            <a
              href={buildWhatsAppLink(CALL_TEXT)}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-call-btn"
              className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[#0F1245]"
            >
              <Phone size={14} strokeWidth={1.6} /> Call Us on WhatsApp
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl">
            {heroStats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="border-t border-white/30 pt-4">
                <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white leading-none">
                  {s.text ? s.text : <Counter to={s.num} suffix={s.suffix} duration={2000} />}
                </div>
                <div className="overline mt-3" style={{ color: "#B7E4F4" }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
